import { ref, watch } from 'vue'

const SIDEBAR_COLLAPSED_KEY = 'scrivio_sidebar_collapsed'

// Shared state across the app
const isCollapsed = ref(loadInitialState())

// Whether the global "Hoofdmenu" nav group is collapsed. Driven by which context
// owns the sidebar — a client dossier (#4256) or Organisatiebeheer (#4380): the
// context's own sub-nav takes focus, so the global menu is collapsed by default
// and can be re-expanded via its chevron. The exception is a dossier on the
// expanded sidebar, which has room for both and starts expanded (#4568).
// Not persisted — it is a per-context default, reset on entering a context,
// moving to another one, or leaving for a screen no context owns. `SideMenu`
// owns that rule; a rail toggle is not a context change and does not re-derive it.
const isHoofdmenuCollapsed = ref(false)

function loadInitialState(): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    // Default to the slim icon-only rail (collapsed) when the user has no stored
    // preference yet, per the UI refresh. An explicit choice is always honoured.
    return stored === null ? true : stored === 'true'
  } catch {
    return true
  }
}

// Persist state changes to localStorage
watch(isCollapsed, (value) => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value))
  } catch {
    // localStorage not available, ignore
  }
})

export function useSidebarState() {
  function toggle() {
    isCollapsed.value = !isCollapsed.value
  }

  function collapse() {
    isCollapsed.value = true
  }

  function expand() {
    isCollapsed.value = false
  }

  function toggleHoofdmenu() {
    isHoofdmenuCollapsed.value = !isHoofdmenuCollapsed.value
  }

  function setHoofdmenuCollapsed(value: boolean) {
    isHoofdmenuCollapsed.value = value
  }

  return {
    isCollapsed,
    toggle,
    collapse,
    expand,
    isHoofdmenuCollapsed,
    toggleHoofdmenu,
    setHoofdmenuCollapsed
  }
}
